import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Truck, Calendar, Phone, User, Loader2, Save, 
  Zap, Car, Plus, Trash2, ChefHat, Users, Info, 
  Check, AlertCircle, CheckCircle, Clock, RefreshCw
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import debounce from "lodash/debounce";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MonInfoHECPage() {
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [myHecInfos, setMyHecInfos] = useState([]);
  const [selectedPlanning, setSelectedPlanning] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [activeTab, setActiveTab] = useState("infos");
  const [selectedMenuJour, setSelectedMenuJour] = useState("Jeudi");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load user's HEC info
  useEffect(() => {
    loadMyHecInfo();
  }, []);

  const loadMyHecInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/hec/my-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyHecInfos(response.data);
      
      // Auto-select first planning if available
      if (response.data.length > 0 && !selectedPlanning) {
        selectPlanning(response.data[0]);
      }
    } catch (error) {
      console.error("Error loading HEC info:", error);
      if (error.response?.status === 404) {
        toast.info("Vous n'êtes associé à aucun planning HEC pour le moment");
      } else {
        toast.error("Erreur lors du chargement de vos informations HEC");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectPlanning = (planningInfo) => {
    setSelectedPlanning(planningInfo);
    setEditingData({ ...planningInfo.food_truck });
    setHasUnsavedChanges(false);
  };

  // Debounced auto-save function
  const debouncedSave = useCallback(
    debounce(async (data, planningId) => {
      try {
        setSaving(true);
        await axios.put(
          `${API_URL}/hec/my-info/${planningId}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setLastSaved(new Date());
        setHasUnsavedChanges(false);
        // Show discrete save indicator, not toast
      } catch (error) {
        console.error("Auto-save error:", error);
        toast.error("Erreur lors de la sauvegarde automatique");
      } finally {
        setSaving(false);
      }
    }, 1500), // Wait 1.5s after last change before saving
    [token]
  );

  // Handle field changes with auto-save
  const handleFieldChange = (field, value) => {
    const newData = { ...editingData, [field]: value };
    setEditingData(newData);
    setHasUnsavedChanges(true);
    
    if (selectedPlanning) {
      debouncedSave(newData, selectedPlanning.planning_id);
    }
  };

  // Handle nested field changes (e.g., menu items, equipe)
  const handleNestedChange = (field, index, subfield, value) => {
    const newArray = [...(editingData[field] || [])];
    if (!newArray[index]) newArray[index] = {};
    newArray[index] = { ...newArray[index], [subfield]: value };
    handleFieldChange(field, newArray);
  };

  // Add employee to equipe
  const addEmployee = () => {
    const newEquipe = [...(editingData.equipe || []), { nom: "", prenom: "", role: "", telephone: "" }];
    handleFieldChange("equipe", newEquipe);
  };

  // Remove employee
  const removeEmployee = (index) => {
    const newEquipe = (editingData.equipe || []).filter((_, i) => i !== index);
    handleFieldChange("equipe", newEquipe);
  };

  // Menu management for specific day
  const getMenuForDay = (jour) => {
    return editingData?.menu_par_jour?.[jour] || editingData?.menu_items || [];
  };

  const updateMenuForDay = (jour, menuItems) => {
    const newMenuParJour = { ...editingData.menu_par_jour, [jour]: menuItems };
    handleFieldChange("menu_par_jour", newMenuParJour);
  };

  const addMenuItem = (jour) => {
    const currentMenu = getMenuForDay(jour);
    const newMenu = [...currentMenu, { nom: "", description: "", prix: 0, allergenes: "" }];
    updateMenuForDay(jour, newMenu);
  };

  const removeMenuItem = (jour, index) => {
    const currentMenu = getMenuForDay(jour);
    const newMenu = currentMenu.filter((_, i) => i !== index);
    updateMenuForDay(jour, newMenu);
  };

  const updateMenuItem = (jour, index, field, value) => {
    const currentMenu = [...getMenuForDay(jour)];
    currentMenu[index] = { ...currentMenu[index], [field]: field === "prix" ? parseFloat(value) || 0 : value };
    updateMenuForDay(jour, currentMenu);
  };

  // Manual save button
  const handleManualSave = async () => {
    if (!selectedPlanning || !editingData) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/hec/my-info/${selectedPlanning.planning_id}`,
        editingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      toast.success("Informations sauvegardées !");
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Chargement de vos informations HEC...</span>
      </div>
    );
  }

  if (myHecInfos.length === 0) {
    return (
      <div className="p-6">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-lg font-semibold text-amber-800 mb-2">Accès Non Autorisé</h2>
            <p className="text-amber-700">
              Vous n'avez pas accès au module "Mon Info HEC" pour le moment.<br />
              L'administrateur doit activer votre accès dans la gestion des droits HEC.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <Truck className="w-6 h-6 text-primary" />
            Mes Informations HEC
          </h1>
          <p className="text-muted-foreground">Complétez vos informations et menus pour l'événement HEC</p>
        </div>
        
        {/* Save Status Indicator */}
        <div className="flex items-center gap-4">
          {saving ? (
            <div className="flex items-center gap-2 text-amber-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Sauvegarde...</span>
            </div>
          ) : lastSaved ? (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Sauvegardé à {format(lastSaved, "HH:mm", { locale: fr })}</span>
            </div>
          ) : hasUnsavedChanges ? (
            <div className="flex items-center gap-2 text-amber-600">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Modifications non sauvegardées</span>
            </div>
          ) : null}
          
          <Button onClick={handleManualSave} disabled={saving || !hasUnsavedChanges}>
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Planning Selector (if multiple) */}
      {myHecInfos.length > 1 && (
        <Card className="mb-6 border-[#E5E0DC]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Sélectionner un événement</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {myHecInfos.map((info) => (
                <Button
                  key={info.planning_id}
                  variant={selectedPlanning?.planning_id === info.planning_id ? "default" : "outline"}
                  onClick={() => selectPlanning(info)}
                  className="flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  {info.planning_nom}
                  <Badge variant="secondary" className="ml-1">
                    {info.date_debut} - {info.date_fin}
                  </Badge>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Planning Info */}
      {selectedPlanning && editingData && (
        <>
          <Card className="mb-6 bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-secondary">{selectedPlanning.planning_nom}</h2>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {selectedPlanning.date_debut} - {selectedPlanning.date_fin}
                  </p>
                </div>
                <Badge className="bg-primary text-white">
                  <Truck className="w-3 h-3 mr-1" />
                  {editingData.nom}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="infos" className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                Informations
              </TabsTrigger>
              <TabsTrigger value="equipe" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Équipe
              </TabsTrigger>
              <TabsTrigger value="menus" className="flex items-center gap-2">
                <ChefHat className="w-4 h-4" />
                Menus
              </TabsTrigger>
            </TabsList>

            {/* Informations Tab */}
            <TabsContent value="infos">
              <div className="grid gap-6">
                {/* Informations générales */}
                <Card className="border-[#E5E0DC]">
                  <CardHeader className="bg-[#F9F7F5] border-b border-[#E5E0DC]">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Truck className="w-4 h-4 text-primary" />
                      Informations générales
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label>Nom du Food Truck</Label>
                        <Input value={editingData.nom || ""} disabled className="bg-gray-50" />
                        <p className="text-xs text-muted-foreground mt-1">Non modifiable</p>
                      </div>
                      <div>
                        <Label>Spécialité *</Label>
                        <Input 
                          value={editingData.specialite || ""} 
                          onChange={(e) => handleFieldChange("specialite", e.target.value)}
                          placeholder="Ex: Thaï, Burger, Pizza..."
                        />
                      </div>
                      <div>
                        <Label>Responsable</Label>
                        <Input 
                          value={editingData.responsable || ""} 
                          onChange={(e) => handleFieldChange("responsable", e.target.value)}
                          placeholder="Nom du responsable"
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input 
                          value={editingData.telephone || ""} 
                          onChange={(e) => handleFieldChange("telephone", e.target.value)}
                          placeholder="+33 6 12 34 56 78"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input 
                          type="email"
                          value={editingData.email || ""} 
                          onChange={(e) => handleFieldChange("email", e.target.value)}
                          placeholder="contact@foodtruck.fr"
                        />
                      </div>
                      <div>
                        <Label>Logo (URL)</Label>
                        <Input 
                          value={editingData.logo_url || ""} 
                          onChange={(e) => handleFieldChange("logo_url", e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Véhicule & Équipement */}
                <Card className="border-[#E5E0DC]">
                  <CardHeader className="bg-green-50 border-b border-green-200">
                    <CardTitle className="text-base flex items-center gap-2 text-green-700">
                      <Car className="w-4 h-4" />
                      Véhicule & Équipement
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label>Type de véhicule</Label>
                        <Select 
                          value={editingData.type_vehicule || ""} 
                          onValueChange={(v) => handleFieldChange("type_vehicule", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Camion">Camion</SelectItem>
                            <SelectItem value="Remorque">Remorque</SelectItem>
                            <SelectItem value="Triporteur">Triporteur</SelectItem>
                            <SelectItem value="Stand">Stand</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Longueur (m)</Label>
                        <Input 
                          value={editingData.longueur || ""} 
                          onChange={(e) => handleFieldChange("longueur", e.target.value)}
                          placeholder="Ex: 6.5"
                        />
                      </div>
                      <div>
                        <Label>Largeur (m)</Label>
                        <Input 
                          value={editingData.largeur || ""} 
                          onChange={(e) => handleFieldChange("largeur", e.target.value)}
                          placeholder="Ex: 2.2"
                        />
                      </div>
                      <div>
                        <Label>Hauteur (m)</Label>
                        <Input 
                          value={editingData.hauteur || ""} 
                          onChange={(e) => handleFieldChange("hauteur", e.target.value)}
                          placeholder="Ex: 3.0"
                        />
                      </div>
                      <div>
                        <Label>Plaque d'immatriculation</Label>
                        <Input 
                          value={editingData.plaque_immatriculation || ""} 
                          onChange={(e) => handleFieldChange("plaque_immatriculation", e.target.value)}
                          placeholder="AB-123-CD"
                        />
                      </div>
                      <div>
                        <Label>Puissance électrique</Label>
                        <Input 
                          value={editingData.puissance_electrique || ""} 
                          onChange={(e) => handleFieldChange("puissance_electrique", e.target.value)}
                          placeholder="Ex: 3PC 16 A"
                        />
                      </div>
                      <div>
                        <Label>Ouverture</Label>
                        <Select 
                          value={editingData.ouverture || ""} 
                          onValueChange={(v) => handleFieldChange("ouverture", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Côté passager">Côté passager</SelectItem>
                            <SelectItem value="Côté conducteur">Côté conducteur</SelectItem>
                            <SelectItem value="Arrière">Arrière</SelectItem>
                            <SelectItem value="Latéral">Latéral</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <input 
                          type="checkbox" 
                          id="auvent"
                          checked={editingData.auvent || false}
                          onChange={(e) => handleFieldChange("auvent", e.target.checked)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="auvent" className="cursor-pointer">Auvent déployable</Label>
                      </div>
                    </div>
                    
                    {/* Véhicule personnel */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
                      <div>
                        <Label>Véhicule personnel (si applicable)</Label>
                        <Input 
                          value={editingData.vehicule_personnel || ""} 
                          onChange={(e) => handleFieldChange("vehicule_personnel", e.target.value)}
                          placeholder="Marque/Modèle"
                        />
                      </div>
                      <div>
                        <Label>Plaque VP</Label>
                        <Input 
                          value={editingData.plaque_vp || ""} 
                          onChange={(e) => handleFieldChange("plaque_vp", e.target.value)}
                          placeholder="CD-456-EF"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Équipe Tab */}
            <TabsContent value="equipe">
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                      <Users className="w-4 h-4" />
                      Équipe ({(editingData.equipe || []).length} personnes)
                    </CardTitle>
                    <Button size="sm" variant="outline" onClick={addEmployee}>
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {(editingData.equipe || []).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Aucun membre d'équipe ajouté</p>
                      <Button size="sm" variant="outline" className="mt-4" onClick={addEmployee}>
                        <Plus className="w-4 h-4 mr-1" />
                        Ajouter un membre
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(editingData.equipe || []).map((emp, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-center p-3 bg-gray-50 rounded-lg">
                          <div className="col-span-3">
                            <Input 
                              placeholder="Prénom" 
                              value={emp.prenom || ""} 
                              onChange={(e) => handleNestedChange("equipe", index, "prenom", e.target.value)}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input 
                              placeholder="Nom" 
                              value={emp.nom || ""} 
                              onChange={(e) => handleNestedChange("equipe", index, "nom", e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input 
                              placeholder="Rôle" 
                              value={emp.role || ""} 
                              onChange={(e) => handleNestedChange("equipe", index, "role", e.target.value)}
                            />
                          </div>
                          <div className="col-span-3">
                            <Input 
                              placeholder="Téléphone" 
                              value={emp.telephone || ""} 
                              onChange={(e) => handleNestedChange("equipe", index, "telephone", e.target.value)}
                            />
                          </div>
                          <div className="col-span-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700"
                              onClick={() => removeEmployee(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Menus Tab */}
            <TabsContent value="menus">
              <Card className="border-[#E5E0DC]">
                <CardHeader className="bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                      <ChefHat className="w-4 h-4" />
                      Gestion des Menus
                    </CardTitle>
                    <div className="flex gap-2">
                      {["Jeudi", "Vendredi", "Samedi Midi", "Samedi Soir"].map((jour) => (
                        <Button
                          key={jour}
                          size="sm"
                          variant={selectedMenuJour === jour ? "default" : "outline"}
                          onClick={() => setSelectedMenuJour(jour)}
                          className={jour === "Samedi Soir" ? "border-purple-300 hover:bg-purple-50" : ""}
                        >
                          {jour}
                          {jour === "Samedi Soir" && <span className="ml-1 text-xs">(Soirée)</span>}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Info banner for Samedi Soir */}
                  {selectedMenuJour === "Samedi Soir" && (
                    <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-2">
                      <Info className="w-5 h-5 text-purple-600" />
                      <span className="text-sm text-purple-700">
                        <strong>Menu Samedi Soir</strong> — Menu spécial pour la soirée, <strong>sans prix affiché</strong> (inclus dans l'événement).
                      </span>
                    </div>
                  )}

                  {/* Menu items */}
                  <div className="space-y-4">
                    {getMenuForDay(selectedMenuJour).map((item, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg bg-white">
                        <div className="grid grid-cols-12 gap-3 items-start">
                          <div className={selectedMenuJour === "Samedi Soir" ? "col-span-6" : "col-span-4"}>
                            <Label>Nom du plat *</Label>
                            <Input 
                              value={item.nom || ""} 
                              onChange={(e) => updateMenuItem(selectedMenuJour, index, "nom", e.target.value)}
                              placeholder="Ex: Burger Classic"
                            />
                          </div>
                          {selectedMenuJour !== "Samedi Soir" && (
                            <div className="col-span-2">
                              <Label>Prix (€)</Label>
                              <Input 
                                type="number"
                                step="0.5"
                                value={item.prix || ""} 
                                onChange={(e) => updateMenuItem(selectedMenuJour, index, "prix", e.target.value)}
                                placeholder="12"
                              />
                            </div>
                          )}
                          <div className={selectedMenuJour === "Samedi Soir" ? "col-span-5" : "col-span-5"}>
                            <Label>Description / Ingrédients</Label>
                            <Input 
                              value={item.description || item.ingredients || ""} 
                              onChange={(e) => updateMenuItem(selectedMenuJour, index, "description", e.target.value)}
                              placeholder="Bœuf, salade, tomate, oignon..."
                            />
                          </div>
                          <div className="col-span-1 pt-6">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-red-500 hover:text-red-700"
                              onClick={() => removeMenuItem(selectedMenuJour, index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="col-span-12">
                            <Label>Allergènes</Label>
                            <Input 
                              value={item.allergenes || ""} 
                              onChange={(e) => updateMenuItem(selectedMenuJour, index, "allergenes", e.target.value)}
                              placeholder="Ex: Gluten, Lait, Œufs..."
                              className="text-orange-600"
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Add menu item button */}
                    <Button 
                      variant="outline" 
                      className="w-full border-dashed"
                      onClick={() => addMenuItem(selectedMenuJour)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter un plat pour {selectedMenuJour}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
